<template>
    <div class="q-pt-none q-px-none">
        <div class="consistentText">
            {{ Translate('This date range filter allows you to specify dynamic time periods by setting start and end points relative to today (i.e. filter based on a range that adjusts in relation to the current date and time).') }}
        </div>
        <div class="q-mt-sm consistentText">
            {{ Translate('Examples:') }}
        </div>
        <ul class="consistentText">
            <li>
                {{ Translate('Last 7 Days: By setting the From value as "7 days ago" and the To value as "now," the filter will include data from the past 7 days up to the current moment.') }}
            </li>
            <li>
                {{ Translate('Last 30 Days: Similarly, setting the From as "30 days ago" and the To as "now" captures data from the past 30 days.') }}
            </li>
        </ul>
        <div class="q-mt-md customGrid q-px-none">
            <!-- Row 1 -->
            <q-select outlined dense hide-bottom-space
                        style="width:100%;"
                        :options="relative_units"
                        option-label="text"
                        option-value="value"
                        emit-value
                        map-options
                        v-model="units_from"
                        @update:model-value="updateReport"
                        bg-color="white">
            </q-select>
            <q-input v-if="units_from == 'abs' && units_from !== 'now'"
                            style="grid-column: span 2;"
                            outlined dense hide-bottom-space
                            v-model="abs_from"
                            type="datetime-local"
                            step="900"
                            :label="Translate('From')"
                            @update:model-value="updateReport"
                            bg-color="white">
            </q-input>
            <q-input v-if="units_from != 'abs' && units_from !== 'now'"
                            outlined dense hide-bottom-space
                            type="number"
                            :label="Translate('From')"
                            v-model.number="value_from"
                            @update:model-value="updateReport"
                            bg-color="white">
            </q-input>
            <div class="row q-ma-none" v-if="units_from !== 'now' && units_from !== 'abs'">
                <q-checkbox dense hide-bottom-space color="primary"
                            class="q-my-none q-mr-sm q-pa-none text-no-wrap"
                            :label="Translate('Round to the') + ' ' + friendlyFromUnit"
                            v-model="round_from"
                            @update:model-value="updateReport">
                </q-checkbox>
                <q-icon name="mdi-information" size="sm" class="cursor-pointer form-hint-icon">
                    <q-tooltip :delay="50" anchor="bottom middle" self="top middle" transition-show="scale" transition-hide="scale">
                        <span style="font-size: larger;">
                            {{ Translate('When checked, the starting value of the range will be rounded down to the nearest') }} {{ friendlyFromUnit }}. {{ Translate('When unchecked, the starting value of the range will be calculated from now.') }}
                        </span>
                    </q-tooltip>
                </q-icon>
            </div>
        
            <!-- Row 2 -->
            <q-select style="grid-row:2; width: 100%;"
                        outlined dense hide-bottom-space
                        :options="relative_units"
                        option-label="text"
                        option-value="value"
                        emit-value
                        map-options
                        v-model="units_to"
                        @update:model-value="updateReport"
                        bg-color="white">
            </q-select>
            <q-input v-if="units_to != 'abs' && units_to !== 'now'"
                            style="grid-row:2;"
                            outlined dense hide-bottom-space
                            type="number"
                            :label="Translate('To')"
                            v-model.number="value_to"
                            @update:model-value="updateReport"
                            bg-color="white">
            </q-input>
            <q-input v-if="units_to == 'abs' && units_to !== 'now'"
                            style="grid-row:2; grid-column:span 2"
                            outlined dense hide-bottom-space
                            v-model="abs_to"
                            type="datetime-local"
                            step="900"
                            :label="Translate('To')"
                            @update:model-value="updateReport"
                            bg-color="white">
            </q-input>
            <div class="row q-ma-none" v-if="units_to !== 'now' && units_to !== 'abs'" style="grid-row:2;">
                <q-checkbox class="q-my-none q-mr-sm q-pa-none text-no-wrap"
                            dense hide-bottom-space color="primary"
                            :label="Translate('Round to the') + ' ' + friendlyToUnit"
                            v-model="round_to"
                            @update:model-value="updateReport">
                </q-checkbox>
                <q-icon name="mdi-information" size="sm" class="cursor-pointer form-hint-icon">
                    <q-tooltip :delay="50" anchor="bottom middle" self="top middle">
                        {{ Translate('When checked, the ending value of the range will be rounded down to the nearest') }} {{ friendlyToUnit }}. {{ Translate('When unchecked, the ending value of the range will be calculated from now.') }}
                    </q-tooltip>
                </q-icon>
            </div>
        </div>
        <div class="q-mt-md q-mb-sm consistentText">
            {{ Translate('Sample result') }}
        </div>
        <div class="row q-col-gutter-xs sampleResultRow">
            <div class="col-12 col-sm-5">
                <q-input class="sampleResultInput"
                                dense
                                borderless
                                readonly
                                hide-bottom-space
                                bg-color="transparent"
                                :model-value="computeCurrentStart">
                    <template v-slot:prepend>
                        <span class="sampleResultLabel">
                            {{ Translate('From:') }}
                        </span>
                    </template>
                </q-input>
            </div>
            <div class="col-12 col-sm-5 sampleResultToCol">
                <q-input class="sampleResultInput"
                                dense
                                borderless
                                readonly
                                hide-bottom-space
                                bg-color="transparent"
                                :model-value="computeCurrentEnd">
                    <template v-slot:prepend>
                        <span class="sampleResultLabel">
                            {{ Translate('To:') }}
                        </span>
                    </template>
                </q-input>
            </div>
        </div>

        <div class="row q-ma-none" v-if="false">
            <div class="col-12 col-sm-6">
                <q-input filled rounded dense hide-bottom-space label="Local From" :model-value="computeLocalStart" readonly></q-input>
            </div>

            <div class="col-12 col-sm-6">
                <q-input filled rounded dense hide-bottom-space label="Local To" :model-value="computeLocalEnd" readonly></q-input>
            </div>
        </div>
        <div class="q-mt-lg consistentText">
            {{ Translate('Advantages:') }}
        </div>
        <ul class="consistentText">
            <li>
                {{ Translate('Dynamic Adjustment: The range selected updates automatically as time passes, ensuring the filter always reflects latest data.') }}
            </li>
            <li>
                {{ Translate('Flexibility: Users can customize the range to suit their needs, whether for recent trends, historical analysis, or specific periods of interest') }}
            </li>
        </ul>
    </div>
</template>

<script>
    import _ from 'lodash';
    import utils from '@/Shared/utils.jsx';
    import filters from '@/Shared/filters.js';
    import common from './common.js';
    import methods from '@/Shared/methods';

    export default {
        // Required in Vue 3 so parent @change is the component emit, not a native
        // DOM listener — otherwise blur/click on the grid delivers an Event and
        // localUpdateFilterValue treats Event.type as filter criteria (CCDEV-18136).
        emits: ['change'],
        data: function () {
            return {
                value_from: '',
                value_to: 0,
                units_from: 'd',
                units_to: 'now',
                round_from: true,
                round_to: true,
                abs_from: '',
                abs_to: '',
            }
        },
        props: {
            item: Object,
            value: null,
            EventBus: null,
        },
        created() {
            const self = this;
            this.updateReportDebouncer = _.debounce(function () {
                if (!self.isActive(self.units_from, self.value_from, self.abs_from) || !self.isActive(self.units_to, self.value_to, self.abs_to)) {
                    self.$emit('change', {});
                    return;
                }

                self.$emit('change', {
                    filterType: 'number',
                    type: 'inRange',
                    filter: self.getRelativeQuery(self.units_from, self.value_from, self.abs_from, self.round_from),
                    filterTo: self.getRelativeQuery(self.units_to, self.value_to, self.abs_to, self.round_to)
                });
            }, 500);

            if (!this.value || Object.keys(this.value).length < 1) {
                // Default values for the date filter
                const now = new Date();
                const year = now.getFullYear();
                let mon = `${now.getMonth()}`; if (mon.length < 2) mon = '0' + mon;
                let day = `${now.getDate()}`; if (day.length < 2) day = '0' + day;

                this.abs_from = `${year}-${mon}-${day}T00:00`; // '2018-06-12T19:30'; // new Date().toISOString();
                this.abs_to = this.abs_from;
            }
            else {
                this.valueChanged(this.value);
            }

            //this.EventBus.$on(`${this.item.ColId}_changed`, this.valueChanged);
        },
        //unmounted() {
        //    this.EventBus.$off(`${this.item.ColId}_changed`, this.valueChanged);
        //},
        watch: {
            value_from() { this.updateReport(); },
            value_to() { this.updateReport(); },
            units_from() { this.updateReport(); },
            units_to() { this.updateReport(); },
            round_from() { this.updateReport(); },
            round_to() { this.updateReport(); },
            abs_from() { this.updateReport(); },
            abs_to() { this.updateReport(); },
            value: {
                handler(newValue) {
                        this.syncFromProps(newValue);
                },
                deep: true,
                immediate: true 
            },
        },
        computed: {
            relative_units: function () {
                return [
                    { text: this.Translate('Now'), value: 'now' },
                    { text: this.Translate('Seconds ago'), value: 's' },
                    { text: this.Translate('Minutes ago'), value: 'm' },
                    { text: this.Translate('Hours ago'), value: 'h' },
                    { text: this.Translate('Days ago'), value: 'd' },
                    { text: this.Translate('Weeks ago'), value: 'w' },
                    { text: this.Translate('Months ago'), value: 'M' },
                    { text: this.Translate('Years ago'), value: 'y' },
                    { text: this.Translate('Absolute'), value: 'abs' },
                ];
            },
            computeCurrentStart: function() {
                return dayjs(common.getRelativeDate(this.units_from, this.value_from, this.abs_from, this.round_from)).format();
            },
            computeCurrentEnd: function () {
                return dayjs(common.getRelativeDate(this.units_to, this.value_to, this.abs_to, this.round_to, true)).format();
            },
            friendlyFromUnit: function() {
                return this.getFriendlyUnit(this.units_from);
            },
            friendlyToUnit: function () {
                return this.getFriendlyUnit(this.units_to);
            },

            computeLocalStart: function () {
                if (this.abs_from)
                    return common.ISO8601ToLocal(this.abs_from);
                return '';
            },
            computeLocalEnd: function () {
                if (this.abs_to)
                    return common.ISO8601ToLocal(this.abs_to);
                return '';
            },
        },
        methods: {
            ...methods,
              syncFromProps(value) {
                if (!value || Object.keys(value).length === 0 || value.cleared == 1 ||  (value.filterType === 'set' && (!value.values || value.values.length === 0)) ) {
                    // Do nothing here
                    // const now = new Date();
                    // const year = now.getFullYear();
                    // let mon = `${now.getMonth()}`; if (mon.length < 2) mon = '0' + mon;
                    // let day = `${now.getDate()}`; if (day.length < 2) day = '0' + day;

                    // this.abs_from = `${year}-${mon}-${day}T00:00`; // '2018-06-12T19:30'; // new Date().toISOString();
                    // this.abs_to = this.abs_from;
                }
            },
            valueChanged(val) {
                if (val.filter == 'now')
                    this.units_from = 'now';
                else if (val.filter && val.filter.substring(0, 3) == 'now') {
                    const { value, units, round } = common.parseFilter(val.filter);
                    this.value_from = value;
                    this.units_from = units;
                    this.round_from = round;
                }
                else if (val.filter && val.filter.includes('T')) {
                    this.units_from = 'abs';
                    this.abs_from = common.ISO8601ToLocal(val.filter);
                }

                if (val.filterTo == 'now')
                    this.units_to = 'now';
                else if (val.filterTo && val.filterTo.substring(0, 3) == 'now') {
                    const { value, units, round } = common.parseFilter(val.filterTo);
                    this.value_to = value;
                    this.units_to = units;
                    this.round_to = round;
                }
                else if (val.filterTo && val.filterTo.includes('T')) {
                    this.units_to = 'abs';
                    this.abs_to = common.ISO8601ToLocal(val.filterTo);
                }
            },
            updateReport() {
                this.updateReportDebouncer();
            },
            applyDateRangeHandler(range) {
                console.log('applyDateRangeHandler');
                console.log(range.startDate);
                console.log(range.endDate);
    
                //common.startDate = range.startDate;
                //common.endDate = range.endDate;

                //utils.log(`Saving localStorage(${this.storageKey}-daterange, ${common.startDate} - ${common.endDate})`);
                //this.System.LocalStorage(this.storageKey + '-daterange', `${common.startDate} - ${common.endDate}`);
                //this.Refresh();
            },
            clearDateRangeHandler() {
            },

            formatDate(date){
                return filters.f_date(date, 'medium');
            },
            getFriendlyUnit(unit) {
                switch (unit) {
                    case 's': return this.Translate('second');
                    case 'm': return this.Translate('minute');
                    case 'h': return this.Translate('hour');
                    case 'd': return this.Translate('day');
                    case 'w': return this.Translate('week');
                    case 'M': return this.Translate('month');
                    case 'y': return this.Translate('year');
                    default: return '';
                }
            },
            getWeek(date) {
                let yearStart = new Date(date.getFullYear(), 0, 1);
                let today = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                let dayOfYear = ((today - yearStart + 1) / 86400000);
                let week = Math.ceil(dayOfYear / 7);
                return week;
            },
            getDateOfWeek(w, y) {
                var d = (1 + (w - 1) * 7); // 1st of January + 7 days for each week

                return new Date(y, 0, d);
            },

            isActive(unit_type, unit_value, abs_value) {
                if (unit_type == 'now')
                    return true;
                else if (unit_type == 'abs')
                    return abs_value !== '';
                else if (unit_value === 0)
                    return true;
                else
                    return unit_value !== '' && unit_value != null;
            },
            getRelativeQuery(unit_type, unit_value, abs_value, round) {
                if (unit_type == 'abs')
                    return new Date(abs_value).toISOString();

                let value = 'now';
                
                if (unit_type != 'now' && unit_value > 0)
                    value += `-${unit_value}${unit_type}`;
                else if (unit_type != 'now' && unit_value < 0)
                    value += `+${-unit_value}${unit_type}`;
                
                if (round && unit_type != 'now')
                    value += '/' + unit_type;

                return value;
            },
        }
    }
</script>

<style scoped lang="scss">
    .verydense {
        max-height: 24px;
        min-height: 24px;
    }

    .mediumdense {
        max-height: 28px;
        min-height: 28px;
    }

    .customGrid {
        display: grid; 
        grid-template-columns: 1fr 4em 1fr; 
        grid-template-rows: auto;
        gap: 8px;
        justify-items: start;
        align-items: center;
    }

    .customGrid :deep(.q-field__label) {
        font-size: 14px;
    }

    .customGrid :deep(.q-field__native),
    .customGrid :deep(.q-field__input) {
        font-size: 16px;
    }

    .consistentText {
        color: var(--v-black-base) !important; 
        opacity: 0.8 !important; 
        font-size: 14px !important; 
        text-rendering: optimizelegibility !important; 
        letter-spacing:0.1 !important;
    }

    .sampleResultRow {
        background-color: color-mix(in srgb, var(--v-primary-base) 12%, transparent);
        padding: 4px 8px 4px 12px;
    }

    .sampleResultToCol {
        margin-left: auto;
    }

    .sampleResultLabel {
        padding-bottom: 2px;
        color: var(--v-secondaryText-base);
        font-size: 16px;
        line-height: 1.25;
    }

    .sampleResultInput :deep(.q-field__control) {
        min-height: 28px;
    }

    .sampleResultInput :deep(.q-field__native),
    .sampleResultInput :deep(.q-field__input) {
        font-size: 16px;
        line-height: 1.25;
        color: rgba(0, 0, 0, 0.87);
    }
</style>